import { AfterViewInit, Component, Input, OnDestroy, SimpleChanges } from '@angular/core';
import { GoogleMapsService } from './google-maps.service';
import { environment } from '@eview/core';
import { AuthHelpers } from '@eview/core/auth';
import { Permission } from '@eview/core/auth/permission';

import { Marker, Position } from '@eview/core/models/map';
import {
  EMapActions,
  SetMapDefaults,
  SetMapRegion,
  SimulateUserClickedMap,
  UserClickedMap,
  UserClickedMarker,
  UserClickedCommentMap
} from '@eview/core/store/actions/map.actions';
import { selectMap } from '@eview/core/store/selectors/map.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as happen from 'happen';
import * as lodash from 'lodash';
import { Subscription } from 'rxjs';

import * as mapIcons from './icons';

import { selectTenant } from '@eview/core/store/selectors/tenant.selector';
import { PostService } from '@eview/core/domain/post/post.service';

declare var google: any;

const MAP_INITIAL_CENTER = { lat: -9.19, lng: -75.0152 };
const MAP_INITIAL_ZOOM: number = 6;
const MAP_ZOOM_MAX: number = 19;
const CREATE_MAP_ID = 'post-create-map';
const VIEW_MAP_ID = 'map-view';
const MOBILE_MAP_ID = 'map-view-mobile';
const MAP_VIEW_DETAIL = 'map-view-detail';
const MAP_VIEW_GET_ALERTS = 'map-view-get-alerts'
const LAT_DEVIATION = 0.90;
const LON_DEVIATION = 1.20;
const DEFAULT_WIDTH = 1367;

@Component({
  selector: 'eview-gmap',
  styleUrls: ['map.componets.scss'],
  template: `
    <div class="map-container" id="map-container" [style.height]="mapId === 'map-view-detail' ? '400px' : '100%'" style="width: 100%; min-height: 300px;">
      <div class="map-frame" style="height: 100%; width: 100%;">
        <div [id]="mapId" style="height: 100%; width: 100%; z-index: 0;" class="font-regular"></div>
      </div>
    </div>
  `
})
export class GMapComponent implements AfterViewInit, OnDestroy {

  @Input() disabled: boolean = false;

  @Input() marker: Position;

  @Input() markersArray: Marker[];

  @Input() getMarkersFromStore: boolean = false;

  @Input() mapId: string;

  private layers: any = [];

  private map: google.maps.Map;

  private initiated: boolean = false;

  private userCanClick: boolean = false;

  private userCursorMarker: google.maps.Marker = null;

  private userCursorProperties: any;

  private radiusCircle: google.maps.Circle = null;

  private markers: Marker[];

  private mapMarkers: google.maps.Marker[] = [];

  private markerClusterer: any;

  private clickedMapMarker: google.maps.Marker;

  private initialZoom: number = null;

  private subs: Subscription;

  private isMapClick: boolean = false;



  AuthHelpers = AuthHelpers;

  ListenerHandle: any;

  markerHandler: any;

  mapMarkerHandler: any;

  Permission = Permission;
  store: Store<AppState>;

  tenant: any;
  tenantName: string;

  constructor(store: Store<AppState>, private actions$: Actions, private googleMapsService: GoogleMapsService, private postService: PostService) {
    this.store = store;
    this.subs = new Subscription();
    this.layers = [];
    this.userCursorProperties = null;
    setTimeout(() => {
      if (this.mapId == MOBILE_MAP_ID && this.map && typeof google !== 'undefined') {
        google.maps.event.trigger(this.map, 'resize');
      }
    }, 500);
  }

  updateRadiusCircle(radiusKm: number) {
    if (this.mapId !== MAP_VIEW_GET_ALERTS) return;
    
    if (this.radiusCircle) {
      this.radiusCircle.setMap(null);
      this.radiusCircle = null;
    }
    
    if (this.userCursorMarker && radiusKm > 0) {
      const position = this.userCursorMarker.getPosition();
      if (position) {
        this.radiusCircle = new google.maps.Circle({
          strokeColor: '#0066FF',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#0066FF',
          fillOpacity: 0.15,
          map: this.map,
          center: position,
          radius: radiusKm * 1000,
          clickable: false
        });
      }
    }
  }

  resetMap() {
    if (this.mapId !== MAP_VIEW_GET_ALERTS) return;
    
    if (this.userCursorMarker) {
      this.userCursorMarker.setMap(null);
      this.userCursorMarker = null;
    }
    
    if (this.radiusCircle) {
      this.radiusCircle.setMap(null);
      this.radiusCircle = null;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.markersArray && typeof this.markersArray !== 'undefined' && typeof google !== 'undefined') {
      this.markers = this.markersArray;
      if (this.map) {
        this.clearMapMarkers();
        const mapMarkers = this.markersArray.map(m => this.buildMapMarker(m));
        mapMarkers.forEach(m => {
          m.addListener('click', () => {
            this.mapMarkerReportClick({ target: m });
          });
        });
        this.mapMarkers = mapMarkers;
        this.setupMarkerClustering();
      }
    }
  }

  ngAfterViewInit() {

    // Inicializar propiedades del cursor
    if (this.mapId !== CREATE_MAP_ID) {
      this.userCursorProperties = null;
    }

    this.subs.add(this.store.select(selectTenant).subscribe(tenant => {
      this.tenant = tenant;
      this.tenantName = tenant.unique_name;
    }));

    // For MAP_VIEW_DETAIL, wait a bit for DOM to be ready
    const initDelay = this.mapId === MAP_VIEW_DETAIL ? 500 : 0;
    
    setTimeout(() => {
      this.googleMapsService.loadGoogleMaps().then(() => {
        this.buildMap();
        this.initMap();
        this.listenToMapEvents();
        this.addShapefiles();
        this.handleAuthorization();
        
        // Handle single marker for MAP_VIEW_DETAIL
        if (this.mapId === MAP_VIEW_DETAIL) {
          setTimeout(() => {
            this.addSingleMarker();
          }, 300);
        }
      }).catch(error => {
        console.error('Error loading Google Maps:', error);
      });
    }, initDelay);
    this.subs.add(
      this.store.select(selectMap).subscribe(map => {
        if (!map) return;

        // Para VIEW_MAP_ID, no reaccionar a cambios de ubicación del cursor
        // Solo manejar marcadores
        if (this.mapId === VIEW_MAP_ID) {
          // Solo procesar marcadores, no la ubicación del cursor
          if (!this.marker && this.getMarkersFromStore && map.markers && typeof this.markersArray === 'undefined' && typeof google !== 'undefined' && this.map && this.initiated) {
            if (!lodash.isEqual(map.markers, this.markers)) {
              this.markers = map.markers;
              this.clearMapMarkers();
              const mapMarkers = map.markers.map(m => this.buildMapMarker(m));
              mapMarkers.forEach(m => {
                m.addListener('click', () => {
                  this.mapMarkerReportClick({ target: m });
                });
              });
              this.mapMarkers = mapMarkers;
              this.setupMarkerClustering();
            }
          }
          return; // No procesar más para VIEW_MAP_ID
        }

        if (
          this.userCanClick &&
          !this.userCursorMarker &&
          this.mapId !== MAP_VIEW_DETAIL &&
          map.actual
        ) {
          let tempLatLon;
          if (this.marker && this.marker.lat && this.marker.lon) {
            tempLatLon = { lat: this.marker.lat, lng: this.marker.lon };
          } else if (map.actual && map.actual.lat && map.actual.lon) {
            tempLatLon = { lat: map.actual.lat, lng: map.actual.lon };
          }
          if (tempLatLon) {
            this.userCursorMarker = this.addMarkerToMap(
              tempLatLon,
              this.map,
              null
            );
          }
        }

        if (!this.marker && this.getMarkersFromStore && map.markers && typeof this.markersArray === 'undefined' && typeof google !== 'undefined' && this.map && this.initiated) {
          if (!lodash.isEqual(map.markers, this.markers)) {
            this.markers = map.markers;
            this.clearMapMarkers();
            const mapMarkers = map.markers.map(m => this.buildMapMarker(m));
            mapMarkers.forEach(m => {
              m.addListener('click', () => {
                this.mapMarkerReportClick({ target: m });
              });
            });
            this.mapMarkers = mapMarkers;
            this.setupMarkerClustering();
          }
        }

        if (this.markersArray && typeof this.markersArray !== 'undefined' && typeof google !== 'undefined') {
          this.markers = this.markersArray;
          this.clearMapMarkers();
          const mapMarkers = this.markersArray.map(m => this.buildMapMarker(m));
          mapMarkers.forEach(m => {
            m.addListener('click', () => {
              this.mapMarkerReportClick({ target: m });
            });
          });
          this.mapMarkers = mapMarkers;
          this.setupMarkerClustering();
        }

      })
    );

    this.subs.add(
      this.actions$
        .pipe(ofType<UserClickedCommentMap>(EMapActions.UserClickedCommentMap))
        .subscribe(action => {
          if (this.mapMarkers) {
            const selectedMarker = this.mapMarkers.find((marker: any) => {
              return marker.feature && marker.feature.properties && marker.feature.properties.id == action.payload;
            });
            if (selectedMarker) {
              this.mapMarkerReportClick({ target: selectedMarker });
            }
          }
        })
    );

    this.subs.add(
      this.actions$
        .pipe(
          ofType<SimulateUserClickedMap>(EMapActions.SimulateUserClickedMap)
        )
        .subscribe(action => {
          // VIEW_MAP_ID no debe reaccionar a SimulateUserClickedMap
          if (this.mapId === VIEW_MAP_ID) return;
          
          if (!action.payload || (action.payload && !action.payload.actual)) {
            if (this.userCursorMarker) {
              this.userCursorMarker.setMap(null);
            }
            // Solo limpiar propiedades si no es CREATE_MAP_ID
            if (this.mapId !== CREATE_MAP_ID) {
              this.userCursorProperties = null;
            }
            this.store.dispatch(
              new UserClickedMap({
                actual: null
              })
            );
            return;
          }
          const { lat, lon, zoom, properties } = action.payload.actual;
          
          // Si viene con propiedades (desde búsqueda), preservarlas
          if (properties && this.mapId === CREATE_MAP_ID) {
            this.userCursorProperties = properties;
          }
          
          // Crear marcador para la ubicación simulada
          if (this.userCursorMarker) {
            this.userCursorMarker.setMap(null);
          }
          
          this.userCursorMarker = this.addMarkerToMap(
            { lat: lat, lng: lon },
            this.map,
            null
          );
          
          // Centrar el mapa en la nueva ubicación
          this.map.setCenter({ lat: lat, lng: lon });
          if (zoom) {
            this.map.setZoom(zoom);
          }
        })
    );
  }

  ngOnDestroy() {
    this.clearMapMarkers();
    if (this.ListenerHandle) {
      google.maps.event.removeListener(this.ListenerHandle);
      this.ListenerHandle = null;
    }
    if (this.markerHandler) {
      this.markerHandler = null;
    }
    if (this.userCursorMarker) {
      this.userCursorMarker.setMap(null);
    }
    if (this.radiusCircle) {
      this.radiusCircle.setMap(null);
    }
    // Solo limpiar estado si no es CREATE_MAP_ID
    if (this.mapId !== CREATE_MAP_ID) {
      this.userCursorProperties = null;
      this.store.dispatch(new UserClickedMap({ actual: null }));
    }
    this.layers = [];
    this.store = null;
    this.subs.unsubscribe();
  }

  private buildMapMarker(marker: Marker, icon: any = null): google.maps.Marker {
    let finalIcon = icon;
    if (!finalIcon) {
      // Asegurar que el marcador tenga el color correcto del estado
      const markerData = {
        color: marker.parent?.color || 'rgb(255, 164, 51)', // Color por defecto Pendiente
        icon: marker.parent?.icon || 'fas fa-map-marker-alt',
        type: marker.type || 'incident'
      };
      finalIcon = this.getGoogleMapsIcon(mapIcons.customIcon(markerData));
    }
    
    const markerOptions: google.maps.MarkerOptions = {
      position: { lat: marker.lat, lng: marker.lon },
      map: this.map
    };
    
    if (finalIcon) {
      markerOptions.icon = finalIcon;
    }
    
    const m = new google.maps.Marker(markerOptions);
    (m as any).feature = { type: null, geometry: null, properties: marker.parent };
    return m;
  }

  private addMarkerToMap(
    marker: any,
    map: google.maps.Map,
    icon: any = undefined
  ): google.maps.Marker {
    let showMarker = null;
    if (marker) {
      if (marker.lat !== undefined && marker.lng !== undefined) {
        const markerOptions: google.maps.MarkerOptions = {
          position: { lat: marker.lat, lng: marker.lng },
          map: map
        };
        
        if (icon) {
          markerOptions.icon = icon;
        }
        
        showMarker = new google.maps.Marker(markerOptions);
      } else {
        showMarker = this.buildMapMarker(marker, icon);
      }
    } else {
    }
    return showMarker;
  }



  private buildMap() {
    const mapElement = document.getElementById(this.mapId);
    
    if (!mapElement) {
      return;
    }
    
    const mapOptions: google.maps.MapOptions = {
      center: MAP_INITIAL_CENTER,
      zoom: MAP_INITIAL_ZOOM,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.LEFT_BOTTOM
      },
      scrollwheel: false,
      draggable: true,
      gestureHandling: 'auto',
      maxZoom: MAP_ZOOM_MAX,
      mapTypeControl: this.mapId === VIEW_MAP_ID,
      mapTypeControlOptions: {
        position: google.maps.ControlPosition.LEFT_BOTTOM
      },
      streetViewControl: false
    };

    this.map = new google.maps.Map(mapElement, mapOptions);
    
    // Para MAP_VIEW_DETAIL, permitir interacción con el mapa
    if (this.mapId === MAP_VIEW_DETAIL) {
      mapElement.style.pointerEvents = 'auto';
    }
  }

  private initMap() {
    // For MAP_VIEW_DETAIL, initialize immediately with marker data
    if (this.mapId === MAP_VIEW_DETAIL) {
      const center = this.marker ? 
        { lat: this.marker.lat, lng: this.marker.lon } : 
        { lat: -12.0464, lng: -77.0428 }; // Lima, Peru as default
      const zoom = this.marker ? 15 : 10;
      
      this.map.setCenter(center);
      this.map.setZoom(zoom);
      this.initiated = true;
      
      // Force multiple resize events
      setTimeout(() => {
        google.maps.event.trigger(this.map, 'resize');
        this.map.setCenter(center);
      }, 100);
      
      setTimeout(() => {
        google.maps.event.trigger(this.map, 'resize');
        this.map.setCenter(center);
      }, 500);
      
      return;
    }
    
    this.subs.add(
      this.store.select(selectMap).subscribe(map => {
        if (!map || this.initiated) return;
        let defaultZoom = map.defaults.zoom  + 0.5;
        let center;
        if (this.mapId === MOBILE_MAP_ID) {
          defaultZoom = (map.defaults.zoom - 0.90);
          center = { lat: map.defaults.lat + LAT_DEVIATION, lng: map.defaults.lon - LON_DEVIATION };
        }
        else if (this.mapId === MAP_VIEW_GET_ALERTS) {
          document.getElementById('map-container').style.height = '300px';
          defaultZoom = 5;
          center = { lat: -9.19, lng: -75.0152 }; // Peru center
        } else {
          defaultZoom = (window.innerWidth > DEFAULT_WIDTH) ? (defaultZoom + 0.50) : defaultZoom;
          center = { lat: map.defaults.lat, lng: map.defaults.lon };
        }
        this.map.setCenter(center);
        this.map.setZoom(defaultZoom);
        this.initiated = true;
        this.initialZoom = defaultZoom;
        google.maps.event.trigger(this.map, 'resize');
      })
    );
    this.subs.add(
      this.actions$
        .pipe(ofType<SetMapDefaults>(EMapActions.SetMapDefaults))
        .subscribe(actions => {
          if (!actions.payload || this.initiated) return;
          this.map.setCenter({
            lat: actions.payload.defaults.lat,
            lng: actions.payload.defaults.lon
          });
          this.map.setZoom(actions.payload.defaults.zoom);
          this.initiated = true;
        })
    );
  }

  private listenToMapEvents() {
    
    if (!this.map) {
      return;
    }
    
    // Don't add click listeners for MAP_VIEW_DETAIL (read-only)
    if (this.mapId !== MAP_VIEW_DETAIL) {
      // Listener principal del mapa
      const mapClickHandler = (event: google.maps.MapMouseEvent) => {
        this.unclickMapMarker();
        if (this.userCursorMarker) {
          this.userCursorMarker.setMap(null);
        }
        if (this.radiusCircle) {
          this.radiusCircle.setMap(null);
          this.radiusCircle = null;
        }
        if (this.userCanClick || this.mapId === CREATE_MAP_ID || this.mapId === MAP_VIEW_GET_ALERTS) {
          
          if (this.mapId !== CREATE_MAP_ID) {
            this.userCursorProperties = null;
          }
          
          setTimeout(() => {
            this.userCursorMarker = this.addMarkerToMap(
              { lat: event.latLng.lat(), lng: event.latLng.lng() },
              this.map,
              null
            );
            
            // Solo despachar la acción si NO es CREATE_MAP_ID
            if (this.mapId !== CREATE_MAP_ID) {
              this.store.dispatch(
                new UserClickedMap({
                  actual: {
                    lat: event.latLng.lat(),
                    lon: event.latLng.lng(),
                    properties: this.userCursorProperties || null
                  }
                })
              );
            }
            
            if (this.mapId === MAP_VIEW_GET_ALERTS) {
              const radiusElement = document.querySelector('input[type="range"]') as HTMLInputElement;
              if (radiusElement) {
                const radiusIndex = parseInt(radiusElement.value);
                const rangeArray = ["1", "5", "10", "20", "50", "100"];
                const radiusKm = parseInt(rangeArray[radiusIndex]);
                
                this.radiusCircle = new google.maps.Circle({
                  strokeColor: '#0066FF',
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: '#0066FF',
                  fillOpacity: 0.15,
                  map: this.map,
                  center: { lat: event.latLng.lat(), lng: event.latLng.lng() },
                  radius: radiusKm * 1000,
                  clickable: false
                });
              }
            }
          }, 100);
        }
      };
      
      this.ListenerHandle = this.map.addListener('click', mapClickHandler);
    }
  }



  private handleAuthorization() {
    
    // MAP_VIEW_DETAIL is always read-only
    if (this.mapId === MAP_VIEW_DETAIL) {
      this.userCanClick = false;
      return;
    }
    
    // For CREATE_MAP_ID and MAP_VIEW_GET_ALERTS, always allow clicks regardless of permissions
    if (this.mapId === CREATE_MAP_ID || this.mapId === MAP_VIEW_GET_ALERTS) {
      this.userCanClick = !this.disabled;
      return;
    }
    
    this.subs.add(
      AuthHelpers.User.HasUserPermission(
        this.store,
        Permission.CreateReport
      ).subscribe(can => {
        if (this.userCanClick !== can && this.userCursorMarker)
          this.userCursorMarker.setMap(null);
        this.userCanClick = !this.disabled && can;
      })
    );
  }

  public unclickMapMarker() {
    if (!this.clickedMapMarker) return;
    const feature = (this.clickedMapMarker as any).feature;
    if (feature && feature.properties) {
      const icon = this.getGoogleMapsIcon(mapIcons.customIcon(feature.properties));
      if (icon) {
        this.clickedMapMarker.setIcon(icon);
      }
    }
    this.clickedMapMarker = null;
  }

  private mapMarkerReportClick(event) {
    this.unclickMapMarker();
    if (this.userCursorMarker) {
      this.userCursorMarker.setMap(null);
    }

    let marker = event.target as google.maps.Marker;
    
    // Ensure the feature object and properties exist
    const feature = (marker as any).feature || { properties: {} };
    const properties = feature.properties;
    
    // Get the original color from the marker properties
    const originalColor = properties.color || 'rgb(255, 164, 51)';
    const clickedIcon = this.getGoogleMapsIcon(mapIcons.clickedIcon(originalColor));
    if (clickedIcon) {
      marker.setIcon(clickedIcon);
    }
    this.clickedMapMarker = marker;

    // Create a new properties object to avoid modifying read-only properties
    const newProperties = {
      ...properties,
      icon: properties.icon || environment.defaults.tagIcon,
      color: properties.color || environment.defaults.tagColor,
    };

    // Assign the new properties object back to the feature properties
    (this.clickedMapMarker as any).feature.properties = newProperties;

    const parent = newProperties || null;
    const position = marker.getPosition();
    this.store.dispatch(
      new UserClickedMarker({
        lat: position.lat(),
        lon: position.lng(),
        parent: { id: parent.id || null }
      })
    );

    marker = null;
  }

  private getGoogleMapsIcon(leafletIcon: any): google.maps.Icon | string | null {
    if (!leafletIcon || !leafletIcon.options || !leafletIcon.options.iconUrl) {
      return null;
    }
    
    return {
      url: leafletIcon.options.iconUrl,
      size: new google.maps.Size(
        leafletIcon.options.iconSize?.[0] || 32,
        leafletIcon.options.iconSize?.[1] || 32
      ),
      anchor: new google.maps.Point(
        leafletIcon.options.iconAnchor?.[0] || 16,
        leafletIcon.options.iconAnchor?.[1] || 32
      ),
      scaledSize: new google.maps.Size(
        leafletIcon.options.iconSize?.[0] || 32,
        leafletIcon.options.iconSize?.[1] || 32
      )
    };
  }



  private clearMapMarkers() {
    if (this.mapMarkers) {
      this.mapMarkers.forEach(marker => marker.setMap(null));
      this.mapMarkers = [];
    }
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
      this.markerClusterer = null;
    }
  }

  private setupMarkerClustering() {
    // No clustering: recalculating clusters on zoom/pan slowed map navigation.
    if (this.mapMarkers && this.mapMarkers.length > 0) {
      this.mapMarkers.forEach(marker => {
        if (!marker.getMap()) {
          marker.setMap(this.map);
        }
      });
    }
  }

  private addShapefiles() {
    // Skip shapefiles for MAP_VIEW_DETAIL (not needed for single marker view)
    if (this.mapId === MAP_VIEW_DETAIL) return;
    
    if (!this.tenant || !this.tenant.map_shapefiles) return;
    
    const shapeFiles = this.tenant.map_shapefiles.map.shapeFiles;
    const configs = shapeFiles.reduce((cfgs, sh) => [...cfgs, ...sh.config], []);
    
    // Usar shpjs como en Leaflet
    const queue: Promise<any>[] = shapeFiles.map(sf => 
      import('shpjs').then(shp => shp.default(sf.uri))
    );
    
    Promise.all(queue).then(data => {
      data.forEach((d) => {
        d.forEach((geoJsonData: any) => {
          const config = configs.find(c => c.fileName === geoJsonData.fileName) || {
            listenForClick: { enabled: false, dispatchProperties: false }
          };
          
          if (config.listenForClick && config.listenForClick.enabled) {
            this.map.data.addGeoJson(geoJsonData);
            
            const shapefileHandler = (event) => {
              if (config.listenForClick.dispatchProperties && event.feature) {
                const properties = {};
                event.feature.forEachProperty((value, key) => {
                  if (value && value.toString().trim() !== '' && value.toString().trim() !== 'null' && value.toString().trim() !== 'undefined') {
                    properties[key] = value;
                  }
                });
                
                if (properties['NOMDEP'] || properties['NOMPRO'] || properties['NOMDIS']) {
                  this.userCursorProperties = properties;
                } else {
                  const filteredProps = {};
                  if (properties['UBI_INEI']) filteredProps['UBI_INEI'] = properties['UBI_INEI'];
                  if (properties['UBI_ONPE']) filteredProps['UBI_ONPE'] = properties['UBI_ONPE'];
                  this.userCursorProperties = Object.keys(filteredProps).length > 0 ? filteredProps : null;
                }
                
                // Manejar el click del mapa aquí también
                if (this.userCanClick || this.mapId === CREATE_MAP_ID || this.mapId === MAP_VIEW_GET_ALERTS) {
                  this.unclickMapMarker();
                  if (this.userCursorMarker) {
                    this.userCursorMarker.setMap(null);
                  }
                  
                  // Obtener las coordenadas correctas del evento
                  const lat = typeof event.latLng.lat === 'function' ? event.latLng.lat() : event.latLng.lat;
                  const lng = typeof event.latLng.lng === 'function' ? event.latLng.lng() : event.latLng.lng;
                  
                  this.userCursorMarker = this.addMarkerToMap(
                    { lat: lat, lng: lng },
                    this.map,
                    null
                  );
                  
                  this.store.dispatch(
                    new UserClickedMap({
                      actual: {
                        lat: lat,
                        lon: lng,
                        properties: this.userCursorProperties || null
                      }
                    })
                  );
                  
                  // Create radius circle for alerts map from shapefile click
                  if (this.mapId === MAP_VIEW_GET_ALERTS) {
                    if (this.radiusCircle) {
                      this.radiusCircle.setMap(null);
                    }
                    const radiusElement = document.querySelector('input[type="range"]') as HTMLInputElement;
                    if (radiusElement) {
                      const radiusIndex = parseInt(radiusElement.value);
                      const rangeArray = ["1", "5", "10", "20", "50", "100"];
                      const radiusKm = parseInt(rangeArray[radiusIndex]);
                      
                      this.radiusCircle = new google.maps.Circle({
                        strokeColor: '#0066FF',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: '#0066FF',
                        fillOpacity: 0.15,
                        map: this.map,
                        center: { lat: lat, lng: lng },
                        radius: radiusKm * 1000,
                        clickable: false
                      });
                    }
                  }
                }
              }
            };
            
            this.map.data.addListener('click', shapefileHandler);
            
            this.map.data.setStyle({
              fillOpacity: 0,
              strokeWeight: 0,
              clickable: true
            });
          }
        });
      });
    }).catch(error => {
      console.log('Error cargando shapefiles:', error);
    });
  }

  private addSingleMarker() {
    if (this.marker && this.marker.lat && this.marker.lon) {
      const markerOptions: google.maps.MarkerOptions = {
        position: { lat: this.marker.lat, lng: this.marker.lon },
        map: this.map
      };
      
      const singleMarker = new google.maps.Marker(markerOptions);
      
      // Center map on marker with appropriate zoom
      this.map.setCenter({ lat: this.marker.lat, lng: this.marker.lon });
      this.map.setZoom(15);
      
      // Force map resize
      setTimeout(() => {
        google.maps.event.trigger(this.map, 'resize');
        this.map.setCenter({ lat: this.marker.lat, lng: this.marker.lon });
      }, 200);
    } else {
      console.log('No se puede crear marcador - datos faltantes:', {
        marker: !!this.marker,
        lat: this.marker?.lat,
        lon: this.marker?.lon
      });
    }
  }





}
